/* eslint-disable react/forbid-prop-types */
import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'tss-react/mui';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';

import { getIn } from 'formik';

const useStyles = makeStyles()((theme) => ({
  formControl: {
    margin: theme.spacing(1),
  },
}));

function debounce(func, wait) {
  let timeout;
  return function debounced(...args) {
    const context = this;
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => {
      timeout = null;
      func.apply(context, args);
    }, wait);
  };
}

// COMPONENT
function FormikAutocomplete({
  label,
  placeholder,
  field,
  form: { touched, errors, setFieldValue },
  getSuggestionLabel,
  onChangeSearchTerm,
  onChange,
}) {
  const { classes } = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceOnChange = useCallback(
    debounce((query) => {
      onChangeSearchTerm(query, setIsLoading).then((resp) => {
        setSuggestions(resp);
      });
    }, 400),
    [onChangeSearchTerm],
  );

  const touch = getIn(touched, field.name);
  const error = getIn(errors, field.name);

  return (
    <Autocomplete
      fullWidth
      openOnFocus={false}
      noOptionsText={`Nenhum resultado.${searchTerm.trim().length === 0 ? ' Digite para buscar...' : ''}`}
      // open={open}
      onInputChange={(event, value) => {
        setSearchTerm(value);
        const currentInputValue = value;
        if (!currentInputValue || currentInputValue.trim().length === 0) {
          setSuggestions([]);
          return;
        }
        debounceOnChange(currentInputValue);
      }}
      value={field.value}
      onChange={(event, value) => {
        setFieldValue(field.name, value);
        onChange(field.name, value);
      }}
      isOptionEqualToValue={(option, value) => option?.id === value?.id}
      getOptionLabel={(option) => (option ? getSuggestionLabel(option) : '')}
      options={suggestions}
      loadingText="Carregando..."
      loading={isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputLabelProps={{
            shrink: true,
          }}
          variant="standard"
          InputProps={{
            autoComplete: 'off',
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          error={touch && error != null}
          helperText={touch && error}
          className={classes.formControl}
        />
      )}
    />
  );
}

FormikAutocomplete.defaultProps = {
  placeholder: null,
};

FormikAutocomplete.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  onChangeSearchTerm: PropTypes.func.isRequired,
  getSuggestionLabel: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  areItemsDifferentChecker: PropTypes.func,
};

FormikAutocomplete.defaultProps = {
  onChange: () => {},
  areItemsDifferentChecker: (prevItem, item) => {
    return (
      (prevItem == null && item != null) ||
      (prevItem != null && item == null) ||
      (prevItem != null && item != null && prevItem.id !== item.id)
    );
  },
};

export default FormikAutocomplete;
